import React from 'react';

import
{
  Icon,
  Container,
  Text,
  Button,
  Input,
  Select,
  Tooltip,
} from '@ui-library';
import { IconName } from '@ui-library/Icon/Icon.type';

import type { DesignSystem, Colors } from './DesignSystemPanel.type';

import { Permissions } from '../../constants';

interface DesignSystemControlsProps {
  designSystem: DesignSystem;
  options: any;
  onUpdate: (designSystem: DesignSystem) => void;
  permissions?: string[];
}

const COLOR_INFO: Record<string, { label: string; description: string; section: string }> = {
  background: {
    label: 'Background',
    description: 'Main background color for your application. Used for the primary canvas and content areas.',
    section: 'base'
  },
  textColor: {
    label: 'Text',
    description: 'Primary text color for headings and body content. Should have good contrast with background.',
    section: 'base'
  },
  mutedBg: {
    label: 'Muted Background',
    description: 'Secondary background color for subtle sections, cards, and containers.',
    section: 'base'
  },
  mutedText: {
    label: 'Muted Text',
    description: 'Secondary text color for less important information, captions, and metadata.',
    section: 'base'
  },
  primaryBg: {
    label: 'Primary Button',
    description: 'Background color for primary action buttons. This is your brand\'s main action color.',
    section: 'buttons'
  },
  primaryText: {
    label: 'Primary Button Text',
    description: 'Text color for primary buttons. Should be readable against the primary button background.',
    section: 'buttons'
  },
  secondaryBg: {
    label: 'Secondary Button',
    description: 'Background color for secondary action buttons and alternative actions.',
    section: 'buttons'
  },
  secondaryText: {
    label: 'Secondary Button Text',
    description: 'Text color for secondary buttons. Should be readable against the secondary button background.',
    section: 'buttons'
  },
  accentBg: {
    label: 'Accent Color',
    description: 'Accent color for highlights, badges, and special elements that need attention.',
    section: 'buttons'
  },
  accentText: {
    label: 'Accent Text',
    description: 'Text color for accent elements. Should be readable against the accent background.',
    section: 'buttons'
  },
  destructiveBg: {
    label: 'Delete/Danger Button',
    description: 'Background color for destructive actions like delete, remove, or cancel. Usually red.',
    section: 'buttons'
  },
  destructiveText: {
    label: 'Danger Button Text',
    description: 'Text color for destructive buttons. Should be readable against the danger background.',
    section: 'buttons'
  },
  input: {
    label: 'Input Background',
    description: 'Background color for form inputs, text fields, and interactive elements.',
    section: 'forms'
  },
  border: {
    label: 'Border',
    description: 'Default border color for inputs, cards, and dividers throughout the application.',
    section: 'forms'
  },
  focusBorder: {
    label: 'Focus Border (Active State)',
    description: 'Border color when an input or element is focused/active. Usually matches primary color.',
    section: 'forms'
  }
};

export const DesignSystemControls: React.FC<DesignSystemControlsProps> = ({
  permissions,
  designSystem,
  options,
  onUpdate
}) => {

  const hasUpdatePermission = permissions?.includes(Permissions.UpdateProjectPermission) ?? false;

  const applyTheme = (themeName: string) => {
    if (!options?.themes) return;
    
    const themeColors = options.themes[themeName]?.colors;
    if (!themeColors) return;

    const updated: DesignSystem = {
      ...designSystem,
      theme_name: themeName,
      colors: {
        ...designSystem.colors,
        ...themeColors
      }
    };

    onUpdate(updated);
  };

  const updateColor = (colorKey: keyof Colors, value: string) => {
   
    const updated: DesignSystem = {
      ...designSystem,
      theme_name: designSystem.theme_name,
      colors: {
        ...designSystem.colors,
        [colorKey]: value
      }
    };

    onUpdate(updated);
  };

  const updateFontSize = (size: string) => {
    if (!options?.fontSizePresets) return;

    const updated: DesignSystem = {
      ...designSystem,
      theme_name: designSystem.theme_name,
      typography: {
        ...designSystem.typography,
        fontSize: size,
        fontSizeValues: options.fontSizePresets[size]
      }
    };

    onUpdate(updated);
  };

  const updateBorderRadius = (value: number) => {

    const updated: DesignSystem = {
      ...designSystem,
      theme_name: designSystem.theme_name,
      borders: {
        radiusRem: value
      }
    };

    onUpdate(updated);
  };

  const updateFontFamily = (fontFamily: string) => {

    const updated: DesignSystem = {
      ...designSystem,
      theme_name: designSystem.theme_name,
      typography: {
        ...designSystem.typography,
        fontFamily
      }
    };

    onUpdate(updated);
  };

  return (
    <div className="space-y-bg-v2-xl" style={{ padding: 'var(--spacing-xl, 24px)' }}>
      {/* Quick Themes */}
      {options?.themes && (
        <div>
          <Text variant="text-sm" weight="semibold" color="primary" className="mb-bg-v2-md">
            Quick Themes
          </Text>
          <div className="grid grid-cols-3 gap-bg-v2-sm">
            {Object.keys(options.themes).map((themeName) => {
              const theme = options.themes[themeName].colors;
              const isActive = designSystem.theme_name === themeName;
              
              return (
                <Button
                  key={themeName}
                  disabled={!hasUpdatePermission}
                  onClick={() => applyTheme(themeName)}
                  variant={isActive ? 'primary' : 'outline'}
                  size="small"
                  className="!p-bg-v2-md !h-auto !justify-start"
                  label={
                    <div className="w-full">
                      <div className="grid grid-cols-2 gap-bg-v2-xs mb-bg-v2-sm h-8">
                        <div className="rounded-bg-v2-xs" style={{ background: theme.primaryBg }}></div>
                        <div className="rounded-bg-v2-xs" style={{ background: theme.background }}></div>
                        <div className="rounded-bg-v2-xs" style={{ background: theme.textColor }}></div>
                        <div className="rounded-bg-v2-xs" style={{ background: theme.mutedBg }}></div>
                      </div>
                      <Text variant="text-xs" weight="medium" color={isActive ? 'white' : 'primary'} className="capitalize">
                        {themeName}
                      </Text>
                    </div>
                  }
                />
              );
            })}
          </div>
        </div>
      )}

      {/* Size & Spacing */}
      <div>
        <Text variant="text-sm" weight="semibold" color="primary" className="mb-bg-v2-md">
          Size & Spacing
        </Text>
        
        <div className="space-y-bg-v2-md">
          <div>
            <Text variant="text-xs" weight="medium" color="secondary" className="mb-bg-v2-sm block">
              Font Size
            </Text>
            <div className="grid grid-cols-3 gap-bg-v2-sm">
              {['small', 'medium', 'large'].map((size) => (
                <Button
                  key={size}
                  disabled={!hasUpdatePermission}
                  label={size.charAt(0).toUpperCase() + size.slice(1)}
                  onClick={() => updateFontSize(size)}
                  variant={designSystem.typography.fontSize === size ? 'primary' : 'outline'}
                  size="very-small"
                  className="w-full"
                />
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Colors */}
      <div>
        <Text variant="text-sm" weight="semibold" color="primary" className="mb-bg-v2-md">
          Colors
        </Text>
        
        {/* Base Colors */}
        <div className="mb-bg-v2-lg">
          <Text variant="text-xs" weight="semibold" color="secondary" className="mb-bg-v2-sm uppercase tracking-wider">
            Base Colors
          </Text>
          <div className="space-y-bg-v2-md">
            {Object.entries(COLOR_INFO)
              .filter(([_, info]) => info.section === 'base')
              .map(([key, info]) => (
                <div key={key} className="flex items-center gap-bg-v2-sm">
                  <input
                    type="color"
                    disabled={!hasUpdatePermission}
                    value={designSystem.colors[key as keyof Colors]}
                    onChange={(e) => updateColor(key as keyof Colors, e.target.value)}
                    className="w-10 h-10 rounded-bg-v2-lg border-2 border-border-secondary cursor-pointer flex-shrink-0 disabled:opacity-50 disabled:cursor-not-allowed"
                  />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-bg-v2-xs mb-bg-v2-xs">
                      <Text variant="text-xs" weight="medium" color="primary">
                        {info.label}
                      </Text>
                      <Tooltip content={info.description} position="top-left" delay={200}>
                        <Button
                          variant="ghost"
                          size="very-small"
                          onClick={() => {}}
                          className="!p-0 !h-auto text-text-quaternary hover:text-text-secondary"
                          label={<Icon name={IconName.InfoCircle} size="xs" variant="secondary" />}
                        />
                      </Tooltip>
                    </div>
                    <Input
                      type="text"
                      value={designSystem.colors[key as keyof Colors]}
                      onChange={(e) => updateColor(key as keyof Colors, e.target.value)}
                      placeholder="#000000"
                      disabled={!hasUpdatePermission}
                      size="sm"
                      className="font-mono"
                    />
                  </div>
                </div>
              ))}
          </div>
        </div>

        {/* Buttons & Actions */}
        <div className="mb-bg-v2-lg">
          <Text variant="text-xs" weight="semibold" color="secondary" className="mb-bg-v2-sm uppercase tracking-wider">
            Buttons & Actions
          </Text>
          <div className="space-y-bg-v2-md">
            {Object.entries(COLOR_INFO)
              .filter(([_, info]) => info.section === 'buttons')
              .map(([key, info]) => (
                <div key={key} className="flex items-center gap-bg-v2-sm">
                  <Container 
                    variant="primary" 
                    padding="none"
                    className="w-10 h-10 rounded-bg-v2-lg border-2 border-border-secondary flex-shrink-0 overflow-hidden"
                  >
                    <input
                      type="color"
                      disabled={!hasUpdatePermission}
                      value={designSystem.colors[key as keyof Colors]}
                      onChange={(e) => updateColor(key as keyof Colors, e.target.value)}
                      className="w-full h-full cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed border-0"
                    />
                  </Container>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-bg-v2-xs mb-bg-v2-xs">
                      <Text variant="text-xs" weight="medium" color="primary">
                        {info.label}
                      </Text>
                      <Tooltip content={info.description} position="top-left" delay={200}>
                        <Button
                          variant="ghost"
                          size="very-small"
                          onClick={() => {}}
                          className="!p-0 !h-auto text-text-quaternary hover:text-text-secondary"
                          label={<Icon name={IconName.InfoCircle} size="xs" variant="secondary" />}
                        />
                      </Tooltip>
                    </div>
                    <Input
                      type="text"
                      value={designSystem.colors[key as keyof Colors]}
                      onChange={(e) => updateColor(key as keyof Colors, e.target.value)}
                      placeholder="#000000"
                      disabled={!hasUpdatePermission}
                      size="sm"
                      className="font-mono"
                    />
                  </div>
                </div>
              ))}
          </div>
        </div>

        {/* Form Fields */}
        <div>
          <Text variant="text-xs" weight="semibold" color="secondary" className="mb-bg-v2-sm uppercase tracking-wider">
            Form Fields
          </Text>
          <div className="space-y-bg-v2-md">
            {Object.entries(COLOR_INFO)
              .filter(([_, info]) => info.section === 'forms')
              .map(([key, info]) => (
                <div key={key} className="flex items-center gap-bg-v2-sm">
                  <Container 
                    variant="primary" 
                    padding="none"
                    className="w-10 h-10 rounded-bg-v2-lg border-2 border-border-secondary flex-shrink-0 overflow-hidden"
                  >
                    <input
                      type="color"
                      disabled={!hasUpdatePermission}
                      value={designSystem.colors[key as keyof Colors]}
                      onChange={(e) => updateColor(key as keyof Colors, e.target.value)}
                      className="w-full h-full cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed border-0"
                    />
                  </Container>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-bg-v2-xs mb-bg-v2-xs">
                      <Text variant="text-xs" weight="medium" color="primary">
                        {info.label}
                      </Text>
                      <Tooltip content={info.description} position="top-left" delay={200}>
                        <Button
                          variant="ghost"
                          size="very-small"
                          onClick={() => {}}
                          className="!p-0 !h-auto text-text-quaternary hover:text-text-secondary"
                          label={<Icon name={IconName.InfoCircle} size="xs" variant="secondary" />}
                        />
                      </Tooltip>
                    </div>
                    <Input
                      type="text"
                      value={designSystem.colors[key as keyof Colors]}
                      onChange={(e) => updateColor(key as keyof Colors, e.target.value)}
                      placeholder="#000000"
                      disabled={!hasUpdatePermission}
                      size="sm"
                      className="font-mono"
                    />
                  </div>
                </div>
              ))}
          </div>
        </div>
      </div>

      {/* Typography */}
      {options?.fontFamilies && (
        <div>
          <Text variant="text-sm" weight="semibold" color="primary" className="mb-bg-v2-md">
            Font
          </Text>
          <Select
            label="Font Family"
            value={designSystem.typography.fontFamily}
            disabled={!hasUpdatePermission}
            onChange={(value) => updateFontFamily(value as string)}
            size="sm"
            options={Object.keys(options.fontFamilies).map((fontName) => ({
              label: fontName,
              value: fontName
            }))}
            labelClassName="text-bg-v2-xs font-bg-v2-medium text-text-secondary"
          />
        </div>
      )}

      {/* Roundness */}
      <div>
        <Text variant="text-sm" weight="semibold" color="primary" className="mb-bg-v2-md">
          Roundness
        </Text>
        <div>
          <Text variant="text-xs" weight="medium" color="secondary" className="mb-bg-v2-sm block">
            Border Radius
          </Text>
          <Container variant="primary" padding="none" className="w-full">
            <input
              type="range"
              min="0"
              max="2"
              step="0.125"
              disabled={!hasUpdatePermission}
              value={designSystem.borders.radiusRem}
              onChange={(e) => updateBorderRadius(parseFloat(e.target.value))}
              className="w-full disabled:opacity-50 disabled:cursor-not-allowed"
            />
          </Container>
          <Text variant="text-xs" weight="medium" color="primary" align="center" className="mt-bg-v2-xs">
            {designSystem.borders.radiusRem.toFixed(3)} rem
          </Text>
        </div>
      </div>
    </div>
  );
};
